(function () {
  'use strict';

  class VideoReels {
    constructor(section) {
      this.section = section;
      this.cards = [...section.querySelectorAll('[data-video-reel-card]')];
      this.track = section.querySelector('[data-video-reels-track]');
      this.dots = section.querySelector('[data-video-reels-dots]');
      this.autoplayEnabled = section.dataset.autoplayVideos === 'true';
      this.playOnHover = section.dataset.playOnHover === 'true';
      this.showMuteButton = section.dataset.showMuteButton === 'true';
      this.hoveringCard = null;

      this.initCards();
      this.initHoverPlay();
      this.initDots();
      this.initAutoplay();
    }

    getVideo(card) {
      return card.querySelector('[data-video-reel-player], video.video-reel-card__video');
    }

    bindInteractiveEvents(element) {
      if (!element) return;
      element.addEventListener('pointerdown', (event) => event.stopPropagation());
      element.addEventListener('click', (event) => event.stopPropagation());
    }

    updateMuteButton(card, video) {
      const muteBtn = card.querySelector('[data-video-reel-mute]');
      if (!muteBtn || !video) return;

      const mutedIcon = muteBtn.querySelector('[data-mute-icon]');
      const unmutedIcon = muteBtn.querySelector('[data-unmute-icon]');
      const isMuted = video.muted;

      muteBtn.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
      muteBtn.setAttribute(
        'aria-label',
        isMuted
          ? muteBtn.dataset.labelUnmute || 'Unmute video'
          : muteBtn.dataset.labelMute || 'Mute video'
      );

      if (mutedIcon) mutedIcon.hidden = !isMuted;
      if (unmutedIcon) unmutedIcon.hidden = isMuted;
      card.classList.toggle('is-muted', isMuted);
      card.classList.toggle('is-unmuted', !isMuted);
    }

    async playVideo(video, card, options = {}) {
      const { muted = false, fromHover = false } = options;
      if (!video || video.tagName !== 'VIDEO') return false;

      this.pauseAllExcept(video, { keepMutedState: fromHover });

      if (!video.getAttribute('src') && video.dataset.videoSrc) {
        video.setAttribute('src', video.dataset.videoSrc);
      }

      if (video.readyState < 2) {
        video.load();
        await new Promise((resolve) => {
          const onReady = () => {
            video.removeEventListener('loadeddata', onReady);
            video.removeEventListener('error', onReady);
            resolve();
          };
          video.addEventListener('loadeddata', onReady, { once: true });
          video.addEventListener('error', onReady, { once: true });
        });
      }

      video.muted = muted;

      try {
        await video.play();
      } catch {
        video.muted = true;
        try {
          await video.play();
        } catch {
          card.classList.remove('is-playing');
          return false;
        }
      }

      card.classList.add('is-playing');
      card.dataset.playingFromHover = fromHover ? 'true' : 'false';
      this.updateMuteButton(card, video);
      return true;
    }

    pauseVideo(card, video, { resetHover = false } = {}) {
      if (!video) return;
      video.pause();
      card.classList.remove('is-playing');
      if (resetHover) {
        delete card.dataset.playingFromHover;
        video.muted = true;
        this.updateMuteButton(card, video);
      }
    }

    initCards() {
      this.cards.forEach((card) => {
        const video = this.getVideo(card);
        const embed = card.querySelector('[data-video-reel-embed]');
        const playBtn = card.querySelector('[data-video-reel-play]');
        const muteBtn = card.querySelector('[data-video-reel-mute]');
        const overlay = card.querySelector('[data-video-reel-overlay]');
        const media = card.querySelector('.video-reel-card__media');

        this.bindInteractiveEvents(media);
        this.bindInteractiveEvents(playBtn);
        this.bindInteractiveEvents(muteBtn);
        card.querySelectorAll('[data-slider-interactive]').forEach((el) => this.bindInteractiveEvents(el));

        if (embed) {
          playBtn?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const iframe = embed.querySelector('[data-video-reel-iframe]');
            if (!iframe) return;

            const embedType = embed.dataset.embedType;
            const embedId = embed.dataset.embedId;
            const isPlaying = card.classList.contains('is-playing');

            if (isPlaying) {
              if (embedType === 'youtube') {
                iframe.src = `https://www.youtube.com/embed/${embedId}?playsinline=1&rel=0&modestbranding=1`;
              } else if (embedType === 'vimeo') {
                iframe.src = `https://player.vimeo.com/video/${embedId}?title=0&byline=0&portrait=0`;
              }
              card.classList.remove('is-playing');
              return;
            }

            this.cards.forEach((otherCard) => {
              if (otherCard === card) return;
              otherCard.classList.remove('is-playing');
              const otherEmbed = otherCard.querySelector('[data-video-reel-embed]');
              const otherIframe = otherEmbed?.querySelector('[data-video-reel-iframe]');
              if (!otherIframe) return;
              const otherType = otherEmbed.dataset.embedType;
              const otherId = otherEmbed.dataset.embedId;
              if (otherType === 'youtube') {
                otherIframe.src = `https://www.youtube.com/embed/${otherId}?playsinline=1&rel=0&modestbranding=1`;
              } else if (otherType === 'vimeo') {
                otherIframe.src = `https://player.vimeo.com/video/${otherId}?title=0&byline=0&portrait=0`;
              }
              const otherVideo = this.getVideo(otherCard);
              if (otherVideo) otherVideo.pause();
            });

            if (embedType === 'youtube') {
              iframe.src = `https://www.youtube.com/embed/${embedId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`;
            } else if (embedType === 'vimeo') {
              iframe.src = `https://player.vimeo.com/video/${embedId}?autoplay=1&title=0&byline=0&portrait=0`;
            }
            card.classList.add('is-playing');
          });
          return;
        }

        if (!video || video.tagName !== 'VIDEO') {
          playBtn?.setAttribute('hidden', '');
          return;
        }

        if (video.getAttribute('src')) {
          video.dataset.videoSrc = video.getAttribute('src');
        }

        video.muted = true;
        this.updateMuteButton(card, video);

        muteBtn?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          video.muted = !video.muted;
          this.updateMuteButton(card, video);
        });

        const togglePlay = async () => {
          const isPlaying = !video.paused && !video.ended;

          if (isPlaying) {
            this.pauseVideo(card, video, { resetHover: true });
            return;
          }

          await this.playVideo(video, card, { muted: false });
        };

        playBtn?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          togglePlay();
        });

        video.addEventListener('click', (event) => {
          event.stopPropagation();
          togglePlay();
        });

        video.addEventListener('play', () => {
          card.classList.add('is-playing');
          this.pauseAllExcept(video);
        });

        video.addEventListener('pause', () => {
          if (this.hoveringCard === card) return;
          card.classList.remove('is-playing');
        });

        video.addEventListener('volumechange', () => {
          this.updateMuteButton(card, video);
        });

        video.addEventListener('ended', () => {
          card.classList.remove('is-playing');
          if (overlay) overlay.style.opacity = '';
        });

        video.addEventListener('error', () => {
          card.classList.remove('is-playing');
        });
      });
    }

    initHoverPlay() {
      if (!this.playOnHover) return;

      this.cards.forEach((card) => {
        const video = this.getVideo(card);
        const media = card.querySelector('.video-reel-card__media');
        if (!video || !media) return;

        media.addEventListener('mouseenter', async () => {
          this.hoveringCard = card;
          await this.playVideo(video, card, { muted: true, fromHover: true });
        });

        media.addEventListener('mouseleave', () => {
          if (this.hoveringCard === card) this.hoveringCard = null;
          this.pauseVideo(card, video, { resetHover: true });
          this.resumeAutoplayIfNeeded(card, video);
        });
      });
    }

    resumeAutoplayIfNeeded(card, video) {
      if (!this.autoplayEnabled || !video) return;

      const rect = card.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
      const ratio = visibleHeight / rect.height;

      if (ratio >= 0.65) {
        this.playVideo(video, card, { muted: true });
      }
    }

    pauseAllExcept(activeVideo, options = {}) {
      const { keepMutedState = false } = options;

      this.cards.forEach((card) => {
        const video = this.getVideo(card);
        if (!video || video === activeVideo || video.tagName !== 'VIDEO') return;
        video.pause();
        if (!keepMutedState) {
          video.muted = true;
          this.updateMuteButton(card, video);
        }
        card.classList.remove('is-playing');
        delete card.dataset.playingFromHover;
      });
    }

    initAutoplay() {
      if (!this.autoplayEnabled) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const card = entry.target;
            if (this.hoveringCard === card) return;

            const video = this.getVideo(card);
            if (!video || video.tagName !== 'VIDEO') return;

            if (entry.isIntersecting && entry.intersectionRatio >= 0.65) {
              this.playVideo(video, card, { muted: true });
            } else {
              this.pauseVideo(card, video, { resetHover: true });
            }
          });
        },
        { threshold: [0.35, 0.65, 0.9] }
      );

      this.cards.forEach((card) => observer.observe(card));
    }

    initDots() {
      if (!this.track || !this.dots) return;

      const slides = [...this.track.querySelectorAll('[data-video-reels-slide]')];
      if (slides.length < 2) {
        this.dots.hidden = true;
        return;
      }

      this.dots.innerHTML = slides
        .map(
          (_, index) =>
            `<button type="button" class="video-reels__dot${index === 0 ? ' is-active' : ''}" data-video-reels-dot="${index}" aria-label="Go to reel ${index + 1}"></button>`
        )
        .join('');

      const updateDots = () => {
        if (!slides.length) return;
        const slideWidth = slides[0].offsetWidth + parseFloat(getComputedStyle(this.track).gap || 16);
        const index = Math.round(this.track.scrollLeft / slideWidth);
        this.dots.querySelectorAll('[data-video-reels-dot]').forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });
      };

      this.dots.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-video-reels-dot]');
        if (!dot) return;
        const index = parseInt(dot.dataset.videoReelsDot, 10);
        const target = slides[index];
        if (target) {
          this.track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        }
      });

      this.track.addEventListener('scroll', () => window.requestAnimationFrame(updateDots), { passive: true });
      updateDots();
    }
  }

  const init = (root = document) => {
    root.querySelectorAll('[data-video-reels]').forEach((section) => {
      if (section.dataset.videoReelsInit === 'true') return;
      section.dataset.videoReelsInit = 'true';
      new VideoReels(section);
      document.dispatchEvent(new CustomEvent('wishlist:updated'));
    });
  };

  window.initVideoReels = init;

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches?.('[data-video-reels]')
      ? event.target
      : event.target.querySelector?.('[data-video-reels]');
    if (!section) return;
    delete section.dataset.videoReelsInit;
    init(section.parentElement || document);
  });
})();
